import { useEffect, useState } from "react";

/**
 * The one debounce interval for type-to-filter search boxes, so every list in the app
 * settles at the same rhythm. Class components that cannot use the hook build their
 * debounced setter on this same constant (e.g. `_.debounce(fn, SEARCH_DEBOUNCE_MS)`).
 */
export const SEARCH_DEBOUNCE_MS: number = 250;

/**
 * Returns `value`, but only after it has stopped changing for `delayMs`. The input stays
 * controlled by the raw value (every keystroke echoes immediately); filtering reads the
 * debounced value, so the list is not re-filtered on every keystroke.
 */
export const useDebouncedValue = <T,>(value: T, delayMs: number = SEARCH_DEBOUNCE_MS): T => {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect((): (() => void) => {
    const timer: ReturnType<typeof setTimeout> = setTimeout((): void => setDebounced(value), delayMs);
    return (): void => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
};
